import { useRef, useEffect, useState } from 'react';
import useStore from '../lib/store';
import Device from './Device';
import ConnectionModal from './ConnectionModal';

export default function Canvas() {
  const canvasRef = useRef(null);
  const [packets, setPackets] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  
  const devices = useStore(state => state.devices);
  const connections = useStore(state => state.connections);
  const selectDevice = useStore(state => state.selectDevice);
  const cancelConnection = useStore(state => state.cancelConnection);

  const handleCanvasClick = (e) => {
    if (e.target === canvasRef.current || e.target.classList.contains('connections-svg')) {
      selectDevice(null);
      cancelConnection();
    }
  };

  const handleConnectionClick = (connection, e) => {
    e.stopPropagation();
    setSelectedConnection(connection);
  };

  const animatePacket = (path) => {
    if (path.length < 2) return;

    const packetId = Date.now() + Math.random();
    const sourceDevice = devices.find(d => d.id === path[0]);
    const destDevice = devices.find(d => d.id === path[path.length - 1]);

    if (!sourceDevice || !destDevice) return;

    const packet = {
      id: packetId,
      x1: sourceDevice.x + 70,
      y1: sourceDevice.y + 50,
      x2: destDevice.x + 70,
      y2: destDevice.y + 50
    };

    setPackets(prev => [...prev, packet]);

    setTimeout(() => {
      setPackets(prev => prev.filter(p => p.id !== packetId));
    }, 800);
  };

  useEffect(() => {
    window.animatePacket = animatePacket;
  }, [devices]);

  return (
    <div
      ref={canvasRef}
      className="canvas"
      onClick={handleCanvasClick}
    >
      <svg className="connections-svg">
        {connections.map(conn => {
          const device1 = devices.find(d => d.id === conn.device1);
          const device2 = devices.find(d => d.id === conn.device2);
          
          if (!device1 || !device2) return null;

          const x1 = device1.x + 70;
          const y1 = device1.y + 50;
          const x2 = device2.x + 70;
          const y2 = device2.y + 50;

          return (
            <g key={conn.id}>
              {/* Clickable invisible wider line */}
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="transparent"
                strokeWidth="20"
                style={{ cursor: 'pointer' }}
                onClick={(e) => handleConnectionClick(conn, e)}
              />
              
              {/* Visible connection line */}
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                className="connection-line"
                style={{ 
                  pointerEvents: 'none'
                }}
              />
            </g>
          );
        })}

        {/* Animated Packets */}
        {packets.map(packet => (
          <g key={packet.id}>
            <circle
              cx={packet.x1}
              cy={packet.y1}
              r="6"
              fill="var(--accent)"
              style={{
                filter: 'drop-shadow(0 0 8px var(--accent))'
              }}
            >
              <animate
                attributeName="cx"
                from={packet.x1}
                to={packet.x2}
                dur="0.8s"
                fill="freeze"
              />
              <animate
                attributeName="cy"
                from={packet.y1}
                to={packet.y2}
                dur="0.8s"
                fill="freeze"
              />
              <animate
                attributeName="opacity"
                from="1"
                to="0"
                dur="0.8s"
                fill="freeze"
              />
            </circle>
          </g>
        ))}
      </svg>

      {devices.map(device => (
        <Device key={device.id} device={device} />
      ))}

      {devices.length === 0 && (
        <div className="canvas-empty-state">
          <div className="empty-state-icon">🖥️</div>
          <h3>No devices yet</h3>
          <p>Click a device on the left to add it to your network</p>
        </div>
      )}

      {/* Connection Details Modal */}
      {selectedConnection && (
        <ConnectionModal 
          connection={selectedConnection}
          onClose={() => setSelectedConnection(null)}
        />
      )}
    </div>
  );
}
